import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import SectionHeading from '@/components/base/SectionHeading';
import DramaCard from '@/components/base/DramaCard';
import ReviewCard from '@/components/base/ReviewCard';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import { dramas } from '@/mocks/dramas';
import { categories } from '@/mocks/categories';
import { reviews } from '@/mocks/reviews';

export default function Home() {
  const featured = dramas.filter((d) => d.featured).slice(0, 5);
  const latest = [...dramas].sort((a, b) => b.airDate.localeCompare(a.airDate)).slice(0, 8);
  const hot = [...dramas].sort((a, b) => b.views - a.views).slice(0, 6);
  const featuredReviews = reviews.slice(0, 3);

  return (
    <SiteLayout>
      <HeroCarousel dramas={featured} />

      {/* Quick category entries */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <SectionHeading title="快捷分类" subtitle="按题材快速进入你感兴趣的内容分区" moreLink="/category" />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/category?genre=${encodeURIComponent(cat.name)}`}
              className="group relative h-32 md:h-40 overflow-hidden rounded-lg border border-background-800 cursor-pointer"
            >
              <img
                src={cat.image}
                alt={`${cat.name} 分类`}
                title={`${cat.name}短剧 - 成人短剧精选`}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/50 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-3">
                <h3 className="text-sm font-semibold text-foreground-50">{cat.name}</h3>
                <p className="text-[11px] text-foreground-400">{cat.count} 部作品</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Latest dramas */}
      <section className="w-full px-4 md:px-8 pb-12 md:pb-16">
        <SectionHeading title="最新上线" subtitle="刚刚更新完结的高质量短篇剧集" moreLink="/category" />
        <div className="flex gap-3 md:gap-4 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4 md:mx-0 md:px-0">
          {latest.map((drama) => (
            <DramaCard key={drama.id} drama={drama} className="w-[150px] md:w-[180px] shrink-0" />
          ))}
        </div>
      </section>

      {/* Hot ranking + Reviews */}
      <section className="w-full px-4 md:px-8 pb-12 md:pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <SectionHeading title="热门排行" subtitle="按累计浏览量实时排序" accent />
            <div className="flex flex-col gap-2.5">
              {hot.map((drama, index) => (
                <Link
                  key={drama.id}
                  to={`/detail/${drama.id}`}
                  className="group flex items-center gap-3 rounded-lg border border-background-800 bg-background-900/60 p-2.5 hover:border-primary-700/70 transition-colors cursor-pointer"
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-sm font-bold ${
                      index < 3 ? 'bg-primary-600 text-background-50' : 'bg-background-800 text-foreground-400'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <div className="h-14 w-10 shrink-0 overflow-hidden rounded-md bg-background-800">
                    <img src={drama.poster} alt={`${drama.title} 海报`} title={`${drama.title} - 成人短剧热门推荐`} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-foreground-50 truncate group-hover:text-primary-300 transition-colors">
                      {drama.title}
                    </h3>
                    <p className="mt-0.5 text-xs text-foreground-500 truncate">{drama.genres.join(' · ')}</p>
                  </div>
                  <span className="shrink-0 text-xs text-foreground-500">
                    <i className="ri-eye-line mr-1" />
                    {(drama.views / 10000).toFixed(1)}万
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <SectionHeading title="影评精选" subtitle="深入剧情的观看视角与解读" moreLink="/reviews" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
              {featuredReviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Intro banner */}
      <section className="w-full px-4 md:px-8 pb-16 md:pb-20">
        <div className="relative overflow-hidden rounded-xl border border-background-800 bg-background-900 px-6 py-10 md:px-12 md:py-14">
          <div className="relative z-10 max-w-3xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading">关于成人短剧</h2>
            <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-300">
              我们专注于精选高质量的成人向短篇影视剧，为 18 岁以上的观众提供专业的剧集导览、演员资料与深度影评。
              从都市情感到悬疑罪案，从文艺治愈到轻喜剧，帮助你快速发现真正值得一看的短篇佳作。
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link
                to="/about"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-accent-600 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-accent-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                了解我们
                <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/reviews"
                className="inline-flex items-center justify-center gap-2 rounded-md border border-background-600 px-5 py-2.5 text-sm font-medium text-foreground-100 hover:border-primary-500 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                阅读影评
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}